export const PRESET_IDS = [
  'concrete',
  'brick',
  'plaster',
  'wood',
  'glass',
  'metal',
  'roof',
  'floor',
  'generic',
] as const;

export type PresetId = (typeof PRESET_IDS)[number];

// First match wins, on the lower-cased name, German and English. Roof before brick so a
// Dachziegel is a roof tile; the generic row keeps hidden build-up layers (studs, insulation,
// air) from naming the element or falling through to its type.
const NAME_RULES: [RegExp, PresetId][] = [
  [/dach|roof/, 'roof'],
  [
    /stud|framing|joist|ständer|staender|lattung|batten|insulation|dämm|daemm|air space|luftschicht|membrane|folie|vapou?r/,
    'generic',
  ],
  [/glas|glazing|verglasung/, 'glass'],
  [/beton|concrete|estrich|screed/, 'concrete'],
  [/ziegel|brick|masonry|mauerwerk|klinker/, 'brick'],
  [/putz|plaster|gips|gypsum|drywall|trockenbau/, 'plaster'],
  [/holz|wood|timber|lumber|plywood|parkett|parquet/, 'wood'],
  [/stahl|steel|metal|alu|zink|zinc|kupfer|copper/, 'metal'],
  [/boden|floor|tile|fliese|keramik|ceramic|stein|stone/, 'floor'],
];

// For elements whose IFC carries no usable material name.
const TYPE_RULES: [RegExp, PresetId][] = [
  [/^ifcroof/, 'roof'],
  [/^ifc(wall|covering|space)/, 'plaster'],
  [/^ifc(slab|footing|pile|column|beam|stair|ramp)/, 'concrete'],
  [/^ifc(window|door|member|furnishing|furniture)/, 'wood'],
  [/^ifc(railing|plate|duct|pipe|cable|flow|distribution|energyconversion)/, 'metal'],
];

export function presetByName(materialName: string): PresetId | null {
  const name = materialName.toLowerCase();
  if (!name.trim()) return null;
  for (const [re, id] of NAME_RULES) if (re.test(name)) return id;
  return null;
}

export function presetByType(ifcType: string): PresetId {
  const type = ifcType.toLowerCase();
  for (const [re, id] of TYPE_RULES) if (re.test(type)) return id;
  return 'generic';
}

export function presetFor(materialName: string, ifcType: string): PresetId {
  return presetByName(materialName) ?? presetByType(ifcType);
}

const solid = (id: PresetId | null) => (id && id !== 'generic' && id !== 'glass' ? id : null);

// The element's own preset: its first material that names one, then its name (Revit type names
// often carry the material), then its IFC type.
export function elementPreset(materialNames: string[], name: string, ifcType: string): PresetId {
  for (const m of materialNames) {
    const id = solid(presetByName(m));
    if (id) return id;
  }
  return solid(presetByName(name)) ?? presetByType(ifcType);
}

// One mesh of an element: its material layer if it slices one, else the element's preset. Glass
// is decided by transparency alone; an opaque part named glass is a frame or a door leaf.
export function partPreset(
  layerName: string | undefined,
  alpha: number,
  element: PresetId,
  ifcType: string,
): PresetId {
  if (alpha < 0.999) return 'glass';
  const id = layerName ? presetFor(layerName, ifcType) : element;
  return id === 'glass' ? presetByType(ifcType) : id;
}
